import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmt = (a) => [a?.address, a?.address2, a?.city, [a?.state, a?.postal_code].filter(Boolean).join(" "), a?.country].filter(Boolean).join(", ");
const fmtOffice = (e) => [e?.office_address, e?.office_city, [e?.office_state, e?.office_zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

function Stat({ label, value, sub, testId }) {
  return <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testId}><p className="text-xs text-slate-500">{label}</p><p className="text-2xl font-semibold text-slate-900">{value}</p>{sub && <p className="text-[11px] text-slate-400">{sub}</p>}</div>;
}

/** Temporary review screen for the Home / Office address migration (legacy address is never modified). */
export default function AddressMigrationReport() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/address-migration").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (r, use_as) => {
    try { await api.post(`/admin/address-migration/${r.person_id}/resolve`, { use_as }); toast.success(`${r.name}: ${use_as === "keep" ? "kept as is" : `saved as ${use_as} address`}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <p className="p-6 text-sm text-slate-400">Loading…</p>;
  const s = d.summary;
  const cls = (b) => Object.entries(b.classifications).map(([k, v]) => `${k} ${v}`).join(", ") || "—";
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="address-migration-report">
      <h1 className="text-2xl font-semibold text-slate-900">Address Migration Review</h1>
      <p className="text-sm text-slate-500 mt-1">The generic Address was split into Home / Personal and Office / Business addresses. The original address stays on every record, unchanged, until review is complete.</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Stat label="Migrated to Office" value={s.migrated_to_office.count} sub={cls(s.migrated_to_office)} testId="am-office" />
        <Stat label="Migrated to Home" value={s.migrated_to_home.count} sub={cls(s.migrated_to_home)} testId="am-home" />
        <Stat label="Already had new address" value={s.already_had} testId="am-already" />
        <Stat label="Address conflicts" value={s.conflict} testId="am-conflict" />
        <Stat label="Needs manual review" value={s.needs_review} testId="am-review" />
        <Stat label="No legacy address" value={s.no_legacy} testId="am-none" />
      </div>
      <div className="mt-6 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="am-review-list">
        {d.review.map((r) => (
          <div key={r.person_id} className="p-3 grid md:grid-cols-[1.2fr_2fr_auto] gap-2 items-center text-sm" data-testid={`am-row-${r.person_id}`}>
            <div><Link to={`/people/${r.person_id}`} className="font-medium text-slate-900 hover:underline">{r.name || "(no name)"}</Link>
              <p className={`text-xs ${r.status === "conflict" ? "text-rose-700" : "text-amber-700"}`}>{r.status === "conflict" ? "Address Conflict — Review Required" : "Address Type Needs Review"}</p>
              <p className="text-[11px] text-slate-400">{r.rule}</p></div>
            <div className="text-xs text-slate-600"><p><span className="text-slate-400">Original address:</span> {fmt(r.legacy)}</p>
              {r.existing && <p><span className="text-slate-400">Current office address:</span> {fmtOffice(r.existing)}</p>}</div>
            <div className="flex flex-wrap gap-1">
              {r.status !== "conflict" && <Button size="sm" variant="outline" onClick={() => resolve(r, "office")} data-testid={`am-office-${r.person_id}`}>Use as Office</Button>}
              <Button size="sm" variant="outline" onClick={() => resolve(r, "home")} data-testid={`am-home-${r.person_id}`}>Use as Home</Button>
              <Button size="sm" variant="ghost" onClick={() => resolve(r, "keep")} data-testid={`am-keep-${r.person_id}`}>Keep as is</Button>
            </div>
          </div>))}
        {!d.review.length && <p className="p-4 text-sm text-slate-500">Nothing left to review.</p>}
      </div>
    </div>);
}
